import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useWebsiteLeads, LeadEntry, LeadType, SubscriptionFormData } from '../../contexts/WebsiteLeadsContext';
import StripePaymentsReviewPanel from './StripePaymentsReviewPanel';
import NewCustomerModal from '../customers/NewCustomerModal';
import { Customer } from '../customers/CustomerInterfaces';

// ---------------------------------------------------------------------------
// Badge config — type label only; status shown inline separately
// ---------------------------------------------------------------------------

const BADGE: Record<LeadType, { label: string; icon: string }> = {
  enquiry:         { label: 'Enquiry', icon: '💬' },
  audit_pending:   { label: 'Audit',   icon: '🛒' },
  audit_submitted: { label: 'Audit',   icon: '📋' },
  audit_paid:      { label: 'Audit',   icon: '🛒' },
  invoice_pending: { label: 'Invoice', icon: '🧾' },
  invoice_paid:    { label: 'Invoice', icon: '🧾' },
  subscription_submitted: { label: 'Subscription', icon: '🌐' },
  subscription_paid:      { label: 'Subscription', icon: '🌐' },
};

const STATUS_PILL: Partial<Record<LeadType, { label: string; className: string }>> = {
  audit_pending:   { label: 'Pending',  className: 'badge-light-warning' },
  audit_submitted: { label: 'Submitted', className: 'badge-light-info' },
  audit_paid:      { label: 'Paid',     className: 'badge-light-success' },
  invoice_pending: { label: 'Pending',  className: 'badge-light-warning' },
  invoice_paid:    { label: 'Received', className: 'badge-light-success' },
  subscription_submitted: { label: 'Lead',   className: 'badge-light-warning' },
  subscription_paid:      { label: 'Active', className: 'badge-light-success' },
};

// Resume-payment link for submitted subscription orders — mints a fresh Stripe
// checkout; if already paid the page tells the customer so.
const RESUME_BASE = 'https://webconstruct.com.au/website-subscription-adelaide/?resume=';

type FilterGroup = 'all' | 'enquiry' | 'subscription' | 'audit' | 'invoice';

const TYPE_FILTERS: { value: FilterGroup; label: string }[] = [
  { value: 'all',          label: 'All' },
  { value: 'enquiry',      label: 'Enquiries' },
  { value: 'subscription', label: 'Subscriptions' },
  { value: 'audit',        label: 'Audits' },
  { value: 'invoice',      label: 'Invoices' },
];

function matchesGroup(lead: LeadEntry, group: FilterGroup): boolean {
  if (group === 'all')          return true;
  if (group === 'enquiry')      return lead.type === 'enquiry';
  if (group === 'audit')        return lead.type === 'audit_pending' || lead.type === 'audit_submitted' || lead.type === 'audit_paid';
  if (group === 'invoice')      return lead.type === 'invoice_pending' || lead.type === 'invoice_paid';
  if (group === 'subscription') return lead.type === 'subscription_submitted' || lead.type === 'subscription_paid';
  return true;
}

const isSubscription = (t: LeadType) => t === 'subscription_submitted' || t === 'subscription_paid';
const isAudit = (t: LeadType) => t === 'audit_pending' || t === 'audit_submitted' || t === 'audit_paid';

// Nice labels for the audit `formData` sections/fields we know about. Anything
// not listed still renders — the key is humanised (weeklyAdminHours → "Weekly
// admin hours") — so new website fields appear automatically, no app change.
const AUDIT_SECTION_LABELS: Record<string, string> = {
  business: 'Business',
  systems:  'Systems & Tools',
  goals:    'Goals & Pain Points',
  profile:  'Profile',
};
const AUDIT_FIELD_LABELS: Record<string, string> = {
  name: 'Business name', website: 'Website', industry: 'Industry', employees: 'Employees',
  yearsInBusiness: 'Years in business', revenueRange: 'Revenue range',
  currentTools: 'Current tools', integrationLevel: 'Integration level',
  weeklyAdminHours: 'Weekly admin hours', manualWorkArea: 'Manual work area',
  topPainPoints: 'Top pain points', desiredOutcomes: 'Desired outcomes', notes: 'Notes',
};
const humanize = (k: string): string =>
  k.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

// Flatten an audit formData map into ordered [sectionLabel, [ [fieldLabel, value] ]]
// dropping empty values. Resilient to unknown sections/fields and non-string leaves.
function auditSections(data?: Record<string, Record<string, any>>): { label: string; rows: [string, string][] }[] {
  if (!data || typeof data !== 'object') return [];
  const out: { label: string; rows: [string, string][] }[] = [];
  for (const [sectionKey, sectionVal] of Object.entries(data)) {
    if (!sectionVal || typeof sectionVal !== 'object') continue;
    const rows: [string, string][] = [];
    for (const [fieldKey, raw] of Object.entries(sectionVal)) {
      const val = raw == null ? '' : String(raw).trim();
      if (!val) continue;
      rows.push([AUDIT_FIELD_LABELS[fieldKey] || humanize(fieldKey), val]);
    }
    if (rows.length) out.push({ label: AUDIT_SECTION_LABELS[sectionKey] || humanize(sectionKey), rows });
  }
  return out;
}

// Ordered render map for the five onboarding sections (labels + human field
// names). Empty values are skipped at render time. `environment` shows provider
// names only — never credentials (handled off-platform).
const FORM_SECTIONS: { key: keyof SubscriptionFormData; label: string; fields: [string, string][] }[] = [
  { key: 'business', label: 'Business', fields: [
    ['businessName', 'Business name'], ['abn', 'ABN'], ['contactName', 'Contact'], ['phone', 'Phone'], ['email', 'Email'],
  ]},
  { key: 'profile', label: 'Profile', fields: [
    ['industry', 'Industry'], ['description', 'Description'], ['serviceArea', 'Service area'], ['openingHours', 'Opening hours'], ['socials', 'Socials'],
  ]},
  { key: 'content', label: 'Content', fields: [
    ['hasExistingSite', 'Has existing site'], ['existingUrl', 'Existing URL'], ['pages', 'Pages'], ['tagline', 'Tagline'], ['keyServices', 'Key services'], ['contentReady', 'Content ready'], ['assetsLink', 'Assets link'],
  ]},
  { key: 'environment', label: 'Environment', fields: [
    ['hasDomain', 'Has domain'], ['domainName', 'Domain'], ['registrar', 'Registrar'], ['hostingProvider', 'Hosting'], ['canDelegate', 'Can delegate'], ['transferDomain', 'Transfer domain'],
  ]},
  { key: 'goals', label: 'Goals', fields: [
    ['goals', 'Goals'], ['competitors', 'Competitors'], ['notes', 'Notes'],
  ]},
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d: Date): string {
  return d.toLocaleString('en-AU', {
    day:    '2-digit',
    month:  'short',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Australia/Adelaide',
  });
}

function timeAgo(d: Date): string {
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Build a prefilled Customer from a submission lead
function leadToCustomer(lead: LeadEntry): Customer {
  const fd = lead.formData;
  // For audits, flatten the collected formData into the notes so the new contact
  // carries the full context ("Business › Industry: …", "Goals › Top pain points: …").
  const auditNote = auditSections(lead.auditData)
    .map((s) => `${s.label}\n${s.rows.map(([k, v]) => `  ${k}: ${v}`).join('\n')}`)
    .join('\n\n');

  const noteParts = [
    lead.formName  ? `Form: ${lead.formName}`   : null,
    lead.subject   ? `Subject: ${lead.subject}` : null,
    lead.message   ? lead.message               : null,
    lead.source    ? `Source: ${lead.source}`   : null,
    lead.storeName ? `${isSubscription(lead.type) ? 'Business' : isAudit(lead.type) ? 'Business' : 'Store'}: ${lead.storeName}` : null,
    lead.storeUrl  ? `URL: ${lead.storeUrl}`    : null,
    fd?.business?.abn     ? `ABN: ${fd.business.abn}`         : null,
    fd?.goals?.goals      ? `Goals: ${fd.goals.goals}`       : null,
    fd?.goals?.notes      ? `Notes: ${fd.goals.notes}`       : null,
    auditNote || null,
  ].filter(Boolean);

  // Industry can come from the subscription profile OR the audit business section.
  const auditIndustry = (lead.auditData?.business?.industry as string) || '';

  return {
    name:          [lead.firstName, lead.lastName].filter(Boolean).join(' '),
    email:         lead.email,
    phone:         lead.phone || '',
    address:       '',
    company:       lead.storeName || '',
    industry:      fd?.profile?.industry || auditIndustry || '',
    notes:         noteParts.join('\n\n'),
    contacts:      [],
    createdAt:     new Date().toISOString().split('T')[0],
    lastContacted: '',
    contactType:   'Lead',
  };
}

// ---------------------------------------------------------------------------
// Entry detail modal
// ---------------------------------------------------------------------------

interface EntryDetailModalProps {
  lead:         LeadEntry | null;
  onClose:      () => void;
  onDelete:     (lead: LeadEntry) => void;
  onCreateContact: (lead: LeadEntry) => void;
}

const EntryDetailModal: React.FC<EntryDetailModalProps> = ({
  lead, onClose, onDelete, onCreateContact,
}) => {
  if (!lead) return null;
  const badge = BADGE[lead.type];
  const status = STATUS_PILL[lead.type];
  const name = [lead.firstName, lead.lastName].filter(Boolean).join(' ');

  return (
    <Modal show={!!lead} onHide={onClose} centered size="lg">
      <Modal.Header closeButton className="border-bottom-0 pb-0">
        <div className="d-flex align-items-center gap-3">
          <span className="fw-bold fs-6 text-gray-800">
            {badge.icon} {badge.label}
          </span>
          {status && (
            <span className={`badge ${status.className} fs-7`}>{status.label}</span>
          )}
          {lead.testMode && <span className="badge badge-light-danger fs-8">TEST · $1</span>}
          <span className="text-muted fs-7">{formatDate(lead.createdAt)}</span>
        </div>
      </Modal.Header>

      <Modal.Body className="pt-4">
        {/* Contact info */}
        <div className="d-flex align-items-start gap-4 mb-6">
          <div className="symbol symbol-60px">
            <div className="symbol-label bg-light-primary fs-2 fw-bold text-primary">
              {(lead.firstName?.[0] || lead.email[0]).toUpperCase()}
            </div>
          </div>
          <div className="flex-grow-1">
            {name && <div className="fw-bolder text-dark fs-4 mb-1">{name}</div>}
            <div className="d-flex flex-wrap gap-4 text-muted fs-7">
              <span>
                <i className="fas fa-envelope me-2" style={{ fontSize: 11 }}></i>
                {lead.email}
              </span>
              {lead.phone && (
                <span>
                  <i className="fas fa-phone-alt me-2" style={{ fontSize: 11 }}></i>
                  {lead.phone}
                </span>
              )}
              {lead.source && (
                <span>
                  <i className="fas fa-globe me-2" style={{ fontSize: 11 }}></i>
                  {lead.source}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="separator mb-6"></div>

        {/* Enquiry details */}
        {lead.type === 'enquiry' && (
          <div>
            {lead.subject && (
              <div className="mb-4">
                <div className="text-muted fs-8 fw-bold text-uppercase mb-1">Subject</div>
                <div className="fw-semibold text-dark fs-6">{lead.subject}</div>
              </div>
            )}
            {lead.message && (
              <div className="mb-4">
                <div className="text-muted fs-8 fw-bold text-uppercase mb-1">Message</div>
                <div
                  className="text-dark fs-7 bg-light rounded p-4"
                  style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
                >
                  {lead.message}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Audit details */}
        {isAudit(lead.type) && (() => {
          const sections = auditSections(lead.auditData);
          return (
            <div>
              {lead.formName && (
                <div className="mb-5">
                  <span className="badge badge-light-primary fs-7">{lead.formName}</span>
                </div>
              )}

              {/* Rich formData sections (automation_audit and any future audit form) */}
              {sections.map((section) => (
                <div className="mb-5" key={section.label}>
                  <div className="text-muted fs-8 fw-bold text-uppercase mb-2">{section.label}</div>
                  <div className="bg-light rounded p-4">
                    <div className="row g-3">
                      {section.rows.map(([label, value]) => (
                        <div className="col-md-6" key={label}>
                          <div className="text-muted fs-9 text-uppercase">{label}</div>
                          <div className="text-dark fs-7" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              {/* Fallback for legacy audits with no formData (e.g. shopify_audit) */}
              {sections.length === 0 && (
                <>
                  {lead.storeName && (
                    <div className="mb-4">
                      <div className="text-muted fs-8 fw-bold text-uppercase mb-1">Store Name</div>
                      <div className="fw-semibold text-dark fs-6">{lead.storeName}</div>
                    </div>
                  )}
                  {lead.storeUrl && (
                    <div className="mb-4">
                      <div className="text-muted fs-8 fw-bold text-uppercase mb-1">Store URL</div>
                      <a href={lead.storeUrl} target="_blank" rel="noopener noreferrer" className="text-primary fs-7">
                        {lead.storeUrl}
                      </a>
                    </div>
                  )}
                  {lead.message && (
                    <div className="mb-4">
                      <div className="text-muted fs-8 fw-bold text-uppercase mb-1">Notes</div>
                      <div className="text-dark fs-7 bg-light rounded p-4" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {lead.message}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* Invoice payment details */}
        {(lead.type === 'invoice_pending' || lead.type === 'invoice_paid') && (
          <div className="d-flex gap-6">
            {lead.invoiceNumber && (
              <div>
                <div className="text-muted fs-8 fw-bold text-uppercase mb-1">Invoice</div>
                <span className="badge badge-light-dark fs-6 fw-bold">{lead.invoiceNumber}</span>
              </div>
            )}
            {lead.amount !== undefined && (
              <div>
                <div className="text-muted fs-8 fw-bold text-uppercase mb-1">Amount</div>
                <div className="fw-bolder text-dark fs-4">
                  ${lead.amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Subscription Website details */}
        {isSubscription(lead.type) && (
          <div>
            {/* Summary line */}
            <div className="d-flex flex-wrap gap-6 mb-5">
              {lead.storeName && (
                <div>
                  <div className="text-muted fs-8 fw-bold text-uppercase mb-1">Business</div>
                  <div className="fw-semibold text-dark fs-6">{lead.storeName}</div>
                </div>
              )}
              {lead.amount != null && (
                <div>
                  <div className="text-muted fs-8 fw-bold text-uppercase mb-1">Plan</div>
                  <div className="fw-bolder text-dark fs-5">
                    ${lead.amount}<span className="fs-8 text-muted fw-semibold"> /mo</span>
                  </div>
                </div>
              )}
              <div>
                <div className="text-muted fs-8 fw-bold text-uppercase mb-1">
                  {lead.type === 'subscription_paid' ? 'Paid' : 'Submitted'}
                </div>
                <div className="fw-semibold text-dark fs-6">
                  {timeAgo(lead.type === 'subscription_paid' && lead.paidAt ? lead.paidAt : lead.createdAt)}
                </div>
              </div>
            </div>

            {/* Resume-payment link for submitted (unpaid) leads */}
            {lead.type === 'subscription_submitted' && (
              <div className="notice d-flex bg-light-warning rounded border-warning border border-dashed p-4 mb-5">
                <i className="fas fa-hourglass-half text-warning me-3 mt-1"></i>
                <div className="flex-grow-1">
                  <div className="fw-bold text-gray-800 mb-1">Awaiting payment</div>
                  <div className="text-gray-700 fs-8 mb-2">
                    Onboarding is complete but payment wasn't finished. Send this link so they can pay
                    (it mints a fresh Stripe checkout; if already paid, the page says so):
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      readOnly
                      className="form-control form-control-sm"
                      value={`${RESUME_BASE}${lead.id}`}
                      onFocus={(e) => e.currentTarget.select()}
                    />
                    <button
                      className="btn btn-sm btn-light-primary text-nowrap"
                      onClick={() => navigator.clipboard?.writeText(`${RESUME_BASE}${lead.id}`)}
                    >
                      <i className="fas fa-copy me-1"></i>Copy
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Five onboarding sections */}
            {FORM_SECTIONS.map((section) => {
              const data = (lead.formData?.[section.key] || {}) as Record<string, string>;
              const rows = section.fields.filter(([k]) => (data[k] || '').trim() !== '');
              if (rows.length === 0) return null;
              return (
                <div className="mb-5" key={section.key}>
                  <div className="text-muted fs-8 fw-bold text-uppercase mb-2">{section.label}</div>
                  <div className="bg-light rounded p-4">
                    <div className="row g-3">
                      {rows.map(([k, label]) => (
                        <div className="col-md-6" key={k}>
                          <div className="text-muted fs-9 text-uppercase">{label}</div>
                          <div className="text-dark fs-7" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {data[k]}
                          </div>
                        </div>
                      ))}
                    </div>
                    {section.key === 'environment' && (
                      <div className="text-muted fs-9 mt-3 fst-italic">
                        Provider names only — access handover is arranged off-platform. No credentials are collected or stored.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-0 d-flex justify-content-between">
        {/* Left: delete */}
        <Button
          variant="light-danger"
          className="btn btn-light-danger btn-sm"
          onClick={() => onDelete(lead)}
        >
          <i className="fas fa-trash-alt me-2"></i>Delete
        </Button>

        {/* Right: create contact (enquiries only) + close */}
        <div className="d-flex gap-2">
          <Button variant="light" onClick={onClose}>
            Close
          </Button>
          {(lead.type === 'enquiry' || isAudit(lead.type) || isSubscription(lead.type)) && (
            <Button
              variant="primary"
              onClick={() => onCreateContact(lead)}
            >
              <i className="fas fa-user-plus me-2"></i>Create Contact
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const WebsiteSubmissionsPage: React.FC = () => {
  const { leads, loading, deleteLead } = useWebsiteLeads();
  const location = useLocation();

  const [typeFilter,    setTypeFilter]    = useState<FilterGroup>('all');
  const [search,        setSearch]        = useState('');

  // Entry detail modal
  const [selectedLead,  setSelectedLead]  = useState<LeadEntry | null>(null);

  // Auto-open modal when navigated here from the panel with a specific lead ID
  useEffect(() => {
    const openLeadId = (location.state as any)?.openLeadId as string | undefined;
    if (!openLeadId || loading) return;
    const [collection, id] = openLeadId.split('::');
    const match = leads.find((l) => l.collection === collection && l.id === id);
    if (match) setSelectedLead(match);
  }, [location.state, leads, loading]);

  // Delete confirmation (can be triggered from detail modal)
  const [pendingDelete, setPendingDelete] = useState<LeadEntry | null>(null);
  const [deleting,      setDeleting]      = useState(false);

  // New contact modal — prefilled from submission
  const [contactDraft,  setContactDraft]  = useState<Customer | null>(null);

  const filtered = useMemo(() => {
    let result = leads;
    result = result.filter((l) => matchesGroup(l, typeFilter));
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((l) =>
        [l.firstName, l.lastName, l.email, l.phone, l.subject, l.message, l.storeName, l.invoiceNumber]
          .some((v) => v?.toLowerCase().includes(q))
      );
    }
    return result;
  }, [leads, typeFilter, search]);

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteLead(pendingDelete);
      setSelectedLead(null);
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  // Called from detail modal — close detail, open NewCustomerModal prefilled
  const handleCreateContact = (lead: LeadEntry) => {
    setSelectedLead(null);
    setContactDraft(leadToCustomer(lead));
  };

  return (
    <div className="container-xxl py-6 px-6">
      {/* Page header */}
      <div className="d-flex align-items-center justify-content-between mb-6">
        <div>
          <h1 className="fw-bolder text-dark mb-1">Website Submissions</h1>
          <div className="text-muted fs-6">
            Real-time feed from <span className="fw-semibold">web-construct-website</span>
          </div>
        </div>
        <span className={`badge ${loading ? 'badge-light-warning' : 'badge-light-success'} fs-7 fw-bold`}>
          <span
            className="bullet bullet-dot me-2"
            style={{ background: loading ? 'var(--bs-warning)' : 'var(--bs-success)' }}
          ></span>
          {loading ? 'Connecting…' : 'Live'}
        </span>
      </div>

      {/* Direct Stripe payments awaiting review (invoice pay-link) */}
      <StripePaymentsReviewPanel />

      {/* Filters */}
      <div className="card mb-5">
        <div className="card-body py-4 px-5 d-flex flex-wrap align-items-center gap-3">
          <div className="position-relative" style={{ width: 240 }}>
            <i
              className="fas fa-search position-absolute top-50 translate-middle-y text-muted"
              style={{ left: 12, fontSize: 12 }}
            ></i>
            <input
              type="text"
              className="form-control form-control-sm ps-8"
              placeholder="Search name, email, subject…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="d-flex flex-wrap gap-2">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.value}
                className={`btn btn-sm ${typeFilter === f.value ? 'btn-primary' : 'btn-light'}`}
                onClick={() => setTypeFilter(f.value)}
              >
                {f.label}
                {f.value !== 'all' && (
                  <span className="ms-2 badge badge-circle badge-sm bg-white text-dark">
                    {leads.filter((l) => matchesGroup(l, f.value)).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading && (
            <div className="p-10 text-center text-muted">
              <span className="spinner-border spinner-border-sm me-2"></span>
              Connecting to website…
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="py-12 text-center text-muted">
              <i className="fas fa-satellite-dish fs-2x mb-4 text-gray-400 d-block"></i>
              {leads.length === 0 ? 'No submissions yet.' : 'No results match your filters.'}
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="table-responsive">
              <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-3 mb-0">
                <thead>
                  <tr className="fw-bolder text-muted bg-light fs-8 text-uppercase">
                    <th className="ps-5 w-200px">Contact</th>
                    <th className="w-160px">Type</th>
                    <th>Details</th>
                    <th className="w-130px text-end">Date</th>
                    <th className="w-50px pe-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lead) => {
                    const key   = `${lead.collection}-${lead.id}`;
                    const badge = BADGE[lead.type];
                    const name  = [lead.firstName, lead.lastName].filter(Boolean).join(' ');

                    return (
                      <tr
                        key={key}
                        className="cursor-pointer"
                        onClick={() => setSelectedLead(lead)}
                        style={{ verticalAlign: 'middle' }}
                      >
                        {/* Contact */}
                        <td className="ps-5 py-4">
                          {name && <div className="fw-bold text-dark fs-7 mb-1">{name}</div>}
                          <div className="text-muted fs-8">{lead.email}</div>
                          {lead.phone && (
                            <div className="text-muted fs-8">
                              <i className="fas fa-phone-alt me-1" style={{ fontSize: 9 }}></i>
                              {lead.phone}
                            </div>
                          )}
                        </td>

                        {/* Type + inline status */}
                        <td className="py-4">
                          <div className="d-flex flex-column gap-1 align-items-start">
                            <span className="fw-semibold fs-7 text-gray-800">
                              {badge.icon} {badge.label}
                            </span>
                            {STATUS_PILL[lead.type] && (
                              <span className={`badge ${STATUS_PILL[lead.type]!.className} fs-8`}>
                                {STATUS_PILL[lead.type]!.label}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Detail preview — single truncated line */}
                        <td className="py-4 pe-4">
                          <div
                            className="text-muted fs-8"
                            style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: 380 }}
                          >
                            {lead.type === 'enquiry' && (lead.subject || lead.message)}
                            {isAudit(lead.type) && [lead.formName, lead.storeName].filter(Boolean).join(' · ')}
                            {(lead.type === 'invoice_pending' || lead.type === 'invoice_paid') &&
                              `${lead.invoiceNumber} — $${lead.amount?.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`
                            }
                            {isSubscription(lead.type) &&
                              [lead.storeName, lead.amount != null ? `$${lead.amount}/mo` : '', lead.testMode ? 'TEST' : '']
                                .filter(Boolean).join(' · ')
                            }
                          </div>
                        </td>

                        {/* Date */}
                        <td className="text-end py-4">
                          <div className="text-muted fs-8">{timeAgo(lead.createdAt)}</div>
                          <div className="text-muted fs-9">{formatDate(lead.createdAt)}</div>
                        </td>

                        {/* Delete */}
                        <td className="pe-4 py-4 text-end" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="btn btn-icon btn-sm btn-active-light-danger"
                            title="Delete"
                            onClick={() => setPendingDelete(lead)}
                          >
                            <i className="fas fa-trash-alt text-danger" style={{ fontSize: 13 }}></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {!loading && filtered.length > 0 && (
          <div className="card-footer py-3 px-5 text-muted fs-8 text-center border-top">
            Showing {filtered.length} of {leads.length} records · live
          </div>
        )}
      </div>

      {/* Entry detail modal */}
      <EntryDetailModal
        lead={selectedLead}
        onClose={() => setSelectedLead(null)}
        onDelete={(lead) => { setSelectedLead(null); setPendingDelete(lead); }}
        onCreateContact={handleCreateContact}
      />

      {/* New contact modal — prefilled from submission */}
      {contactDraft && (
        <NewCustomerModal
          show={!!contactDraft}
          onClose={() => setContactDraft(null)}
          customer={contactDraft}
        />
      )}

      {/* Delete confirmation modal */}
      <Modal
        show={!!pendingDelete}
        onHide={() => !deleting && setPendingDelete(null)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!deleting}>
          <Modal.Title className="fs-5 fw-bolder">Delete Entry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingDelete && (
            <>
              <div className="d-flex align-items-center mb-4">
                <div className="symbol symbol-50px me-4">
                  <div className="symbol-label bg-light-danger">
                    <i className="fas fa-trash-alt text-danger fs-3"></i>
                  </div>
                </div>
                <div>
                  <div className="fw-bold text-dark fs-6">
                    {[pendingDelete.firstName, pendingDelete.lastName].filter(Boolean).join(' ') || pendingDelete.email}
                  </div>
                  <div className="text-muted fs-7">{pendingDelete.email}</div>
                </div>
              </div>
              <div className="notice d-flex bg-light-danger rounded border-danger border border-dashed p-4">
                <i className="fas fa-exclamation-triangle text-danger me-3 mt-1"></i>
                <div className="text-gray-700 fs-7">
                  This will permanently delete the entry from the website database. This action cannot be undone.
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setPendingDelete(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirmed} disabled={deleting}>
            {deleting
              ? <><span className="spinner-border spinner-border-sm me-2"></span>Deleting…</>
              : 'Delete'
            }
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WebsiteSubmissionsPage;
